import { createTheme, Theme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import "@mui/material/styles";

declare module "@mui/material/styles" {
	interface ColorRange {
		50: string;
		100: string;
		200: string;
		300: string;
		400: string;
		500: string;
		600: string;
		700: string;
		800: string;
		900: string;
		950?: string;
	}

	interface Palette {
		orange: PaletteColor & ColorRange;
		gray: PaletteColor & ColorRange;
		hover: string;
		selected: string;
	}

	interface PaletteOptions {
		orange?: PaletteOptions["primary"];
		gray?: PaletteOptions["primary"];
		hover?: string;
		selected?: string;
	}

	interface PaletteColor {
		contrast?: string;
	}
	interface SimplePaletteColorOptions {
		contrast?: string;
	}

	interface TypeBackground {
		paper2: string;
	}

	interface TypeText {
		light: string;
		icon: string;
	}

	interface PaletteAction {
		contrast: string;
	}

	interface TypeAction {
		contrast: string;
	}
}

const baseTheme = createTheme({
	breakpoints: {
		values: {
			xs: 0,
			sm: 600,
			md: 900,
			lg: 1200,
			xl: 1536,
		},
	},
});

export const theme = createTheme(baseTheme, {
	typography: {
		fontFamily: [
			"Inter",
			"-apple-system",
			"BlinkMacSystemFont",
			'"system-ui"',
			"sans-serif",
		].join(","),
		h1: {
			fontSize: "2rem",
			fontWeight: 500,
			"@media (min-width:900px)": {
				fontSize: "2.5rem",
			},
		},
	},
	components: {
		MuiButton: {
			styleOverrides: {
				root: {
					fontSize: "0.875rem",
					padding: "10px 16px",
					borderRadius: 12,
					textTransform: 'none',
					fontWeight: 600,
				},
				containedPrimary: {
					color: "#fff",
					backgroundColor: "rgb(255, 94, 0)",
					'&:hover': {
						backgroundColor: "rgb(230, 85, 0)",
					},
					'&:active': {
						backgroundColor: "rgb(200, 75, 0)",
					},
				},
				outlined: {
					borderColor: "rgba(64, 64, 64, 0.6)",
					color: "rgb(245, 245, 245)",
					'&:hover': {
						borderColor: "rgb(255, 94, 0)",
						backgroundColor: "rgba(255, 94, 0, 0.08)",
					},
					'&:active': {
						backgroundColor: "rgba(255, 94, 0, 0.16)",
					},
				},
				text: {
					'&:hover': {
						backgroundColor: "rgba(255, 255, 255, 0.08)",
					},
					'&:active': {
						backgroundColor: "rgba(255, 255, 255, 0.12)",
					},
				}
			},
		},
		MuiIconButton: {
			styleOverrides: {
				root: {
					color: "rgb(163, 163, 163)", 
					'&:hover': {
						backgroundColor: "rgba(255, 255, 255, 0.08)", 
						color: "rgb(245, 245, 245)", 
					},
					'&:active': {
						backgroundColor: "rgba(255, 255, 255, 0.12)",
					},
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					backgroundImage: "linear-gradient(to bottom right, #262626e6, #171717b3)",
					backdropFilter: "blur(10px)",
				},
			},
		},
		MuiCard: {
			styleOverrides: {
				root: {
					backgroundColor: "rgba(38, 38, 38, 0.4)",
					border: "1px solid rgba(64, 64, 64, 0.6)",
					backgroundImage: "none",
				},
			},
		},
        // Стили для Snackbar
        MuiSnackbarContent: {
            styleOverrides: {
                root: {
                    backgroundColor: "rgba(38, 38, 38, 0.95)", // Почти непрозрачный темный фон
                    color: "rgb(245, 245, 245)",
                    borderRadius: 12,
                    border: "1px solid rgba(64, 64, 64, 0.6)",
                    boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.3)",
                    fontSize: "0.9rem",
                    fontWeight: 500,
                },
                message: {
                    padding: "8px 0",
                },
                action: {
                    marginRight: 0,
                }
            }
        },
		MuiCssBaseline: {
			styleOverrides: (theme: Theme) => ({
				body: {
					background: "linear-gradient(rgb(10, 10, 10) 0%, rgb(26, 26, 26) 50%, rgb(10, 10, 10) 100%)",
					backgroundAttachment: "fixed",
					minHeight: "100vh",
					overflowX: "hidden",
					userSelect: "none",
					WebkitUserSelect: "none",
					scrollbarWidth: 'none !important',
					msOverflowStyle: 'none !important',
					'&::-webkit-scrollbar': {
						display: 'none !important',
					},
				},
				img: {
					WebkitUserDrag: "none",
					userDrag: "none",
				},
				"*": {
					WebkitTapHighlightColor: "transparent",
				},
			}),
		},
		MuiAlert: {
			defaultProps: {
				iconMapping: {
					success: <CheckCircleIcon fontSize="inherit" />,
				},
			},
		},
	},
	
	palette: {
		mode: 'dark',
		
		orange: {
			50: "#fff7ed",
			100: "#ffedd5",
			200: "#fed7aa",
			300: "#fdba74",
			400: "rgb(251, 146, 60)",
			500: "rgb(255, 94, 0)",
			600: "#ea580c",
			700: "#c2410c",
			800: "#9a3412",
			900: "#7c2d12",
		},

		gray: {
			50: "#fafaf9",
			100: "#f5f5f4",
			200: "#e7e5e4",
			300: "#d6d3d1",
			400: "rgb(163, 163, 163)",
			500: "#78716c",
			600: "#57534e",
			700: "#44403c",
			800: "#292524",
			900: "#1c1917",
		},
		
		primary: {
			main: "rgb(255, 94, 0)",
			light: "rgb(251, 146, 60)",
			contrastText: "#ffffff",
		},
		secondary: {
			main: "rgb(163, 163, 163)",
			light: "rgb(212, 212, 212)",
			contrastText: "#ffffff",
		},
		
		background: {
			default: "#0a0a0a",
			paper: "#262626",
			paper2: "#171717",
		},
		
		text: {
			primary: "rgb(245, 245, 245)",
			secondary: "rgb(163, 163, 163)",
			disabled: "rgba(255, 255, 255, 0.3)",
			icon: "rgb(228, 225, 224)",
		},
		
		action: {
			hover: "rgba(255, 255, 255, 0.08)",
			selected: "rgba(255, 94, 0, 0.16)",
			disabled: "rgba(255, 255, 255, 0.3)",
			disabledBackground: "rgba(255, 255, 255, 0.12)",
			focus: "rgba(255, 94, 0, 0.12)",
			contrast: "rgba(255, 255, 255, 0.2)",
		},
		
		divider: "rgba(64, 64, 64, 0.6)",
	},
	shape: {
		borderRadius: 12,
	},
});
